<template>
  <div class="page-layout users contact-page">

    <section class="contact-hero">
      <b-container fluid>
        <b-row class="contact-hero__row intro-section">
          <b-col xl="12">
            <div class="contact-hero__inner text-section">
              <div>
                <h1 class="contact-hero__title mb-3"><b>{{ langDec.contact_page_title }}</b></h1>
                <p class="contact-hero__lead">
                  {{ langDec.contact_hero_lead }}
                </p>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </section>

    <section class="contact-intro greyBox">
      <b-container fluid>
        <b-row>
          <b-col xl="12" class="contact-intro-col">
            <div class="text-section contact-intro__card">
              <h2 class="name contact-intro__heading">{{ langDec.contact_intro_heading }}</h2>
              <p class="contact-intro__text mb-0">
                {{ langDec.contact_intro_body }}
              </p>
              <a class="contact-mail-chip" :href="'mailto:' + officeEmail">{{ officeEmail }}</a>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </section>

    <section class="contact-form-section">
      <b-container>
        <b-form
          class="contact-form"
          :class="{ 'contact-form--rtl': isAr }"
          @submit.stop.prevent="onSubmit"
        >
          <div class="contact-form__card">
            <div v-if="submitLoading" class="contact-form__overlay">
              <div class="contact-form__overlay-inner">
                <b-spinner variant="primary"></b-spinner>
                <span class="contact-form__overlay-text">{{ langDec.contact_overlay_sending }}</span>
              </div>
            </div>

            <b-row>
              <b-col lg="6" md="12" class="mb-3">
                <b-form-group
                  id="group-form-name"
                  :label="langDec.contact_label_name"
                  label-for="form-name"
                  class="contact-form-group"
                >
                  <b-form-input
                    id="form-name"
                    v-model="$v.form.name.$model"
                    :state="validateState('name')"
                    autocomplete="name"
                    class="contact-input"
                  />
                  <b-form-invalid-feedback v-if="$v.form.name.$dirty && !$v.form.name.required">
                    {{ langDec.contact_err_name_required }}
                  </b-form-invalid-feedback>
                  <b-form-invalid-feedback v-if="$v.form.name.$dirty && !$v.form.name.namePattern">
                    {{ langDec.contact_err_name_pattern }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>

              <b-col lg="6" md="12" class="mb-3">
                <b-form-group
                  id="group-form-email"
                  :label="langDec.contact_label_email"
                  label-for="form-email"
                  class="contact-form-group"
                >
                  <b-form-input
                    id="form-email"
                    v-model="$v.form.email.$model"
                    type="email"
                    :state="validateState('email')"
                    autocomplete="email"
                    class="contact-input"
                  />
                  <b-form-invalid-feedback v-if="$v.form.email.$dirty && !$v.form.email.required">
                    {{ langDec.contact_err_email_required }}
                  </b-form-invalid-feedback>
                  <b-form-invalid-feedback v-if="$v.form.email.$dirty && !$v.form.email.email">
                    {{ langDec.contact_err_email_invalid }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>

              <b-col lg="6" md="12" class="mb-3">
                <b-form-group
                  id="group-form-country"
                  :label="langDec.contact_label_country"
                  class="contact-form-group"
                >
                  <div
                    id="form-country"
                    class="contact-country-wrap"
                    :class="{ 'contact-country-wrap--invalid': countrySelectInvalid }"
                  >
                    <multiselect
                      v-model="$v.form.country.$model"
                      class="contact-country-multiselect"
                      :options="countryOptions"
                      :searchable="true"
                      :internal-search="true"
                      :loading="countriesLoading"
                      :disabled="countriesLoading || countryOptions.length === 0"
                      :close-on-select="true"
                      :clear-on-select="false"
                      :preserve-search="false"
                      :show-no-results="true"
                      :hide-selected="false"
                      open-direction="bottom"
                      :max-height="300"
                      :placeholder="langDec.contact_placeholder_country"
                      select-label=""
                      selected-label=""
                      deselect-label=""
                      @open="touchCountry"
                      @input="touchCountry"
                      @close="touchCountry"
                    >
                      <template slot="noResult">
                        <span class="contact-country-slot">{{ langDec.contact_country_no_results }}</span>
                      </template>
                      <template slot="noOptions">
                        <span class="contact-country-slot">{{ langDec.contact_country_no_options }}</span>
                      </template>
                    </multiselect>
                  </div>
                  <div
                    v-if="$v.form.country.$dirty && !$v.form.country.required"
                    class="invalid-feedback d-block"
                  >
                    {{ langDec.contact_err_country_required }}
                  </div>
                </b-form-group>
              </b-col>

              <b-col lg="6" md="12" class="mb-3">
                <b-form-group
                  id="group-form-subject"
                  :label="langDec.contact_label_subject"
                  label-for="form-subject"
                  class="contact-form-group"
                >
                  <b-form-input
                    id="form-subject"
                    v-model="$v.form.subject.$model"
                    :state="validateState('subject')"
                    class="contact-input"
                  />
                  <b-form-invalid-feedback v-if="$v.form.subject.$dirty && !$v.form.subject.required">
                    {{ langDec.contact_err_subject_required }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>

              <b-col lg="12" class="mb-4">
                <b-form-group
                  id="group-form-message"
                  :label="langDec.contact_label_message"
                  label-for="form-message"
                  class="contact-form-group"
                >
                  <b-form-textarea
                    id="form-message"
                    v-model="$v.form.message.$model"
                    rows="6"
                    :state="validateState('message')"
                    class="contact-input contact-textarea"
                  />
                  <b-form-invalid-feedback v-if="$v.form.message.$dirty && !$v.form.message.required">
                    {{ langDec.contact_err_message_required }}
                  </b-form-invalid-feedback>
                  <b-form-invalid-feedback v-if="$v.form.message.$dirty && !$v.form.message.minLength">
                    {{ langDec.contact_err_message_min }}
                  </b-form-invalid-feedback>
                  </b-form-group>
                </b-col>

              <b-col lg="12" v-show="turnstileEnabled" class="mb-3 contact-turnstile-wrap">
                <div ref="turnstileHost" class="contact-turnstile-host" :aria-label="langDec.contact_turnstile_aria"></div>
              </b-col>

              <b-col lg="12" class="contact-form__submit-wrap">
                <b-button
                  type="submit"
                  variant="primary"
                  size="lg"
                  class="contact-submit-btn"
                  :disabled="submitLoading || countriesLoading || submitBlockedByTurnstile"
                >
                  {{ langDec.contact_submit }}
                </b-button>
              </b-col>
            </b-row>
          </div>
        </b-form>
      </b-container>
    </section>
  </div>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, email, minLength, maxLength } from "vuelidate/lib/validators";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import { sendRequest } from '../utils/RequestHelper.js'
import { sendContactFormEmail, isContactEmailConfigured } from '../utils/contactEmail.js'
import { mapGetters } from "vuex"

const namePattern = (value) =>
  value != null && /^[A-Za-z\u0600-\u06FF\u0750-\u077F\s]{2,30}$/.test(String(value).trim());

export default {
  name: 'ContactUsPage',
  mixins: [validationMixin],
  components: {
    Multiselect,
  },
  data() {
    const turnstileKey = String(process.env.VUE_APP_TURNSTILE_SITE_KEY || '').trim();
    return {
      officeEmail: process.env.VUE_APP_CONTACT_EMAIL || 'takhatyofficial@gmail.com',
      countriesLoading: false,
      submitLoading: false,
      /** نسخة من المفتاح في data للتأكد أن الـ bundle قرأ .env بعد إعادة تشغيل serve */
      turnstileSiteKey: turnstileKey,
      form: {
        name: null,
        country: null,
        email: null,
        subject: null,
        message: null,
      },
      countries: [],
      turnstileWidgetId: null,
      turnstileToken: '',
    };
  },
  
  validations: {
    form: {
      name: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(30),
        namePattern,
      },
      email: {
        required,
        email,
      },
      country: {
        required,
      },
      subject: {
        required,
      },
      message: {
        required,
        minLength: minLength(10),
      },
    },
  },
  computed: {
    ...mapGetters(["getLangData", "getlang"]),
    langDec() {
      return this.getLangData || {}
    },
    isAr() {
      return this.getlang === "ar"
    },
    countryOptions() {
      const names = this.countries
        .map((c) => c.name || c.Name || "")
        .filter(Boolean);
      const loc = this.getlang === "ar" ? "ar" : "en"
      return [...new Set(names)].sort((a, b) =>
        a.localeCompare(b, loc, { sensitivity: "base" })
      );
    },
    countrySelectInvalid() {
      const c = this.$v.form.country;
      return c.$dirty && c.$error;
    },
    turnstileEnabled() {
      return this.turnstileSiteKey.length > 0;
    },
    submitBlockedByTurnstile() {
      return this.turnstileEnabled && !this.turnstileToken;
    },
  },
  watch: {
    '$store.state.theme'() {
      if (!this.turnstileEnabled) return;
      this.destroyTurnstile();
      this.$nextTick(() => {
        this.mountTurnstile();
      });
    },
  },
  methods: {
    ensureTurnstileScript() {
      return new Promise((resolve, reject) => {
        if (typeof window === 'undefined') {
          reject(new Error('no window'));
          return;
        }
        const ready = () =>
          Boolean(window.turnstile && typeof window.turnstile.render === 'function');
        if (ready()) {
          resolve();
          return;
        }
        const src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        let existing = document.querySelector(`script[src="${src}"]`);
        if (!existing) {
          const s = document.createElement('script');
          s.src = src;
          s.async = true;
          s.defer = true;
          s.onload = () => (ready() ? resolve() : reject(new Error('turnstile API missing')));
          s.onerror = () => reject(new Error('turnstile script error'));
          document.head.appendChild(s);
          return;
        }
        let n = 0;
        const timer = setInterval(() => {
          if (ready()) {
            clearInterval(timer);
            resolve();
          } else if (n++ > 200) {
            clearInterval(timer);
            reject(new Error('turnstile load timeout'));
          }
        }, 50);
        existing.addEventListener('error', () => {
          clearInterval(timer);
          reject(new Error('turnstile script error'));
        });
      });
    },
    turnstileTheme() {
      return document.documentElement.getAttribute('data-theme') === 'dark'
        ? 'dark'
        : 'light';
    },
    async mountTurnstile() {
      if (!this.turnstileEnabled) return;
      await this.$nextTick();
      await this.$nextTick();
      const host = this.$refs.turnstileHost;
      if (!host) return;

      this.destroyTurnstile();
      const sitekey = this.turnstileSiteKey;
      if (!sitekey) return;

      try {
        await this.ensureTurnstileScript();
      } catch {
        this.$bvToast.toast(
          this.langDec.contact_turnstile_load_fail,
          {
            title: this.langDec.contact_toast_title,
            variant: 'warning',
            solid: true,
            noCloseButton: false,
          }
        );
        return;
      }

      host.innerHTML = '';
      try {
        this.turnstileWidgetId = window.turnstile.render(host, {
          sitekey,
          theme: this.turnstileTheme(),
          callback: (token) => {
            this.turnstileToken = token || '';
          },
          'error-callback': () => {
            this.turnstileToken = '';
            this.$bvToast.toast(this.langDec.contact_turnstile_fail, {
              title: this.langDec.contact_toast_title,
              variant: 'warning',
              solid: true,
            });
          },
          'expired-callback': () => {
            this.turnstileToken = '';
          },
        });
      } catch {
        this.turnstileWidgetId = null;
      }
    },
    destroyTurnstile() {
      if (typeof window !== 'undefined' && window.turnstile && this.turnstileWidgetId != null) {
        try {
          window.turnstile.remove(this.turnstileWidgetId);
        } catch {
          /* ignore */
        }
      }
      this.turnstileWidgetId = null;
      this.turnstileToken = '';
      const host = this.$refs.turnstileHost;
      if (host) {
        host.innerHTML = '';
      }
    },
    resetTurnstileAfterSubmit() {
      if (!this.turnstileEnabled || typeof window === 'undefined' || !window.turnstile) return;
      if (this.turnstileWidgetId != null) {
        try {
          window.turnstile.reset(this.turnstileWidgetId);
        } catch {
          /* ignore */
        }
      }
      this.turnstileToken = '';
    },
    touchCountry() {
      this.$v.form.country.$touch();
    },
    validateState(name) {
      const field = this.$v.form[name];
      if (!field) return null;
      const { $dirty, $error } = field;
      return $dirty ? !$error : null;
    },
    onSubmit() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }
      if (this.turnstileEnabled && !this.turnstileToken) {
        this.$bvToast.toast(this.langDec.contact_turnstile_complete_first, {
          title: this.langDec.contact_toast_title,
          variant: 'warning',
          solid: true,
        });
        return;
      }
      this.createItem();
    },
    getCountries() {
      this.countriesLoading = true;
      sendRequest(
        'get',
        'Countries',
        false,
        null,
        (response) => {
          this.countriesLoading = false;
          this.countries = response.data.data || [];
        },
        () => {
          this.countriesLoading = false;
          this.$bvToast.toast(this.langDec.contact_countries_load_fail, {
            title: this.langDec.contact_toast_error_title,
            variant: 'danger',
            solid: true,
          });
        }
      );
    },
    buildSubmitPayload() {
      return {
        name: this.form.name,
        email: this.form.email,
        country: this.form.country,
        subject: this.form.subject,
        message: this.form.message,
        turnstileToken: this.turnstileToken,
      };
    },
    createItem() {
      if (!isContactEmailConfigured()) {
        this.$bvToast.toast(
          this.langDec.contact_webforms_missing,
          {
            title: this.langDec.contact_toast_title,
            variant: 'warning',
            solid: true,
            noCloseButton: true,
          }
        );
        return;
      }
      this.submitLoading = true;
      sendContactFormEmail(this.buildSubmitPayload())
        .then(() => {
          this.form.email = null;
          this.form.message = null;
          this.form.name = null;
          this.form.country = null;
          this.form.subject = null;
          this.$v.form.$reset();
          this.resetTurnstileAfterSubmit();
          this.$bvToast.toast(this.langDec.contact_success, {
            title: this.langDec.contact_toast_title,
            variant: 'success',
            solid: true,
            noCloseButton: true,
          });
        })
        .catch(() => {
          this.$bvToast.toast(this.langDec.contact_send_error, {
            title: this.langDec.contact_toast_title,
            variant: 'danger',
            solid: true,
            noCloseButton: true,
          });
        })
        .finally(() => {
          this.submitLoading = false;
        });
    },
  },
  created() {
    this.getCountries();
  },
  mounted() {
    this.$nextTick(() => this.mountTurnstile());
  },
  beforeDestroy() {
    this.destroyTurnstile();
  },
};
</script>

<style scoped>
.contact-page {
  background: #f4f7f5;
  padding-bottom: 4rem;
}

.contact-hero__row.intro-section {
  background-image: url(../assets/images/heroGreenBG.png);
  background-size: cover;
  background-position: center;
  min-height: 320px !important;
  color: white;
}

.contact-hero__inner.text-section {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 320px !important;
  padding: 2rem 1rem;
}

.contact-hero__title {
  font-size: clamp(1.75rem, 4vw, 2.35rem) !important;
  font-weight: 800 !important;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.2);
}

.contact-hero__lead {
  max-width: 760px;
  margin: 0 auto;
  font-size: 1.05rem;
  line-height: 1.85;
  opacity: 0.96;
}

.greyBox {
  padding: 40px 24px 24px;
}

.contact-intro-col {
  text-align: start;
}

.contact-intro__card {
  background: linear-gradient(135deg, #ffffff 0%, #f6faf7 100%);
  border-radius: 14px;
  padding: 28px 28px 24px !important;
  border: 1px solid rgba(0, 102, 51, 0.1);
  box-shadow: 0 8px 28px rgba(0, 102, 51, 0.06);
}

.contact-intro__heading {
  color: #006633 !important;
  margin-top: 0 !important;
}

.contact-intro__text {
  font-size: 1rem !important;
  line-height: 1.75;
  color: #495057;
}

.contact-mail-chip {
  display: inline-flex;
  align-items: center;
  margin-top: 1rem;
  padding: 10px 18px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.95rem;
  color: #006633;
  background: rgba(0, 102, 51, 0.09);
  border: 1px solid rgba(0, 102, 51, 0.2);
  text-decoration: none !important;
  transition: background 0.2s ease, transform 0.2s ease;
}

.contact-mail-chip:hover {
  background: rgba(0, 102, 51, 0.14);
  color: #004d26 !important;
  transform: translateY(-1px);
}

.contact-form-section {
  margin-top: 0.5rem;
}

.contact-form__card {
  position: relative;
  max-width: 920px;
  margin: 0 auto;
  padding: clamp(1.5rem, 4vw, 2.25rem);
  background: #fff;
  border-radius: 18px;
  border: 1px solid rgba(0, 102, 51, 0.1);
  box-shadow: 0 12px 44px rgba(0, 102, 51, 0.08);
}

.contact-form__card::before {
  content: "";
  position: absolute;
  inset-inline-start: 0;
  top: 24px;
  bottom: 24px;
  width: 4px;
  border-radius: 4px;
  background: linear-gradient(180deg, #006633 0%, #0d8049 100%);
}

.contact-form__overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.contact-form__overlay-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.contact-form__overlay-text {
  font-weight: 600;
  color: #006633;
}

/* RTL: عناوين الحقول بمحاذاة البداية (يمين في RTL) */
.contact-form--rtl {
  direction: rtl;
  text-align: start;
  unicode-bidi: embed;
}

.contact-form--rtl ::v-deep .contact-form-group,
.form-group.contact-form-group {
  text-align: start;
}

/* Labels */
::v-deep .contact-form-group label,
::v-deep .contact-form-group > div > label,
::v-deep .contact-form-group > legend,
::v-deep .contact-form-group .col-form-label {
  font-weight: 700;
  color: #2d3436;
  margin-bottom: 0.4rem;
  text-align: start;
  display: block;
  width: 100%;
}

.contact-form--rtl .contact-input,
.contact-form--rtl .contact-textarea {
  text-align: start;
}

.contact-form__submit-wrap {
  display: flex;
  justify-content: flex-start;
}

.contact-turnstile-wrap {
  display: flex;
  justify-content: flex-start;
}

.contact-turnstile-host {
  min-height: 4.5rem;
}

.contact-input,
.contact-select {
  border-radius: 10px !important;
  border: 1px solid rgba(0, 102, 51, 0.18) !important;
  padding: 0.65rem 0.9rem !important;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  min-height: 48px !important;
  font-size: 1rem !important;
  line-height: 1.5 !important;
}

.contact-textarea {
  border-radius: 10px !important;
  border: 1px solid rgba(0, 102, 51, 0.18) !important;
  padding: 0.65rem 0.9rem !important;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  resize: vertical;
  min-height: 140px;
}

.contact-input:focus,
.contact-select:focus,
.contact-textarea:focus {
  border-color: #006633 !important;
  box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.15) !important;
}

.contact-submit-btn {
  border-radius: 12px !important;
  font-weight: 700 !important;
  padding: 0.65rem 2.25rem !important;
  background-color: #006633 !important;
  border-color: #006633 !important;
}

.contact-submit-btn:hover:not(:disabled) {
  background-color: #004d26 !important;
  border-color: #004d26 !important;
}

.contact-submit-btn:disabled {
  opacity: 0.65;
}

/* vue-multiselect — بحث + RTL + هوية تخطي */
.contact-country-wrap {
  position: relative;
}

.contact-country-wrap--invalid ::v-deep .multiselect__tags {
  border-color: #dc3545 !important;
}

.contact-country-slot {
  font-size: 0.9rem;
  padding: 6px 12px;
}

.contact-country-wrap ::v-deep .multiselect {
  min-height: 48px !important;
  box-sizing: border-box;
}

.contact-country-wrap ::v-deep .multiselect__select {
  left: 1px;
  right: auto;
  top: 0;
  bottom: 0;
  height: auto;
  padding-top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.contact-country-wrap ::v-deep .multiselect__tags {
  direction: rtl;
  text-align: right;
  min-height: 48px !important;
  height: 48px !important;
  padding: 0 42px 0 14px !important;
  display: flex !important;
  align-items: center !important;
  flex-wrap: nowrap !important;
  box-sizing: border-box;
  border-radius: 10px !important;
  border: 1px solid rgba(0, 102, 51, 0.18) !important;
  background: #fff;
  font-size: 1rem !important;
  line-height: 1.5 !important;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.contact-country-wrap ::v-deep .multiselect__tags:focus-within,
.contact-country-wrap ::v-deep .multiselect--active .multiselect__tags {
  border-color: #006633 !important;
  box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.15);
}

.contact-country-wrap ::v-deep .multiselect.multiselect--active .multiselect__tags {
  height: auto !important;
  min-height: 48px !important;
  align-items: center !important;
}

.contact-country-wrap ::v-deep .multiselect__input,
.contact-country-wrap ::v-deep .multiselect__single {
  direction: rtl;
  text-align: right;
  margin-bottom: 0;
  padding: 0;
  font-size: 1rem !important;
  line-height: 1.5 !important;
  background: transparent;
}

.contact-country-wrap ::v-deep .multiselect__single {
  display: flex;
  align-items: center;
  min-height: 0;
}

.contact-country-wrap ::v-deep .multiselect__input::placeholder {
  color: #8b959e;
}

.contact-country-wrap ::v-deep .multiselect__placeholder {
  color: #8b959e;
  padding-top: 0;
  margin-bottom: 0;
}

.contact-country-wrap ::v-deep .multiselect__content-wrapper {
  direction: rtl;
  text-align: right;
  border-radius: 10px !important;
  border: 1px solid rgba(0, 102, 51, 0.14);
  box-shadow: 0 12px 36px rgba(0, 102, 51, 0.12);
  z-index: 40;
}

.contact-country-wrap ::v-deep .multiselect__option {
  padding: 10px 14px;
  font-size: 0.92rem;
  direction: rtl;
}

.contact-country-wrap ::v-deep .multiselect__option--highlight {
  background: #006633 !important;
}

.contact-country-wrap ::v-deep .multiselect__option--highlight::after {
  display: none;
}

.contact-country-wrap ::v-deep .multiselect__option--selected {
  background: rgba(0, 102, 51, 0.1);
  color: #1a1d1f;
  font-weight: 600;
}

.contact-country-wrap ::v-deep .multiselect__option--selected.multiselect__option--highlight {
  background: #005529 !important;
  color: #fff;
}

.contact-country-wrap ::v-deep .multiselect__spinner {
  left: 12px;
  right: auto;
}

.contact-country-wrap ::v-deep .multiselect--disabled .multiselect__tags {
  opacity: 0.65;
  background: #f7f8f9;
}

@media (max-width: 767.98px) {
  .contact-hero__row.intro-section {
    min-height: 280px !important;
  }

  .contact-hero__inner.text-section {
    min-height: 280px !important;
  }

  .greyBox {
    padding: 32px 16px 16px;
  }

  .contact-form__card::before {
    top: 16px;
    bottom: 16px;
  }
}
</style>
